import { useState, useMemo } from 'react';
import {
  Search,
  Plus,
  Copy,
  Eye,
  Trash2,
  Tag,
  Calendar,
  Users,
  Star,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { usePromoCodes, useDeactivatePromoCode } from '@/hooks/usePromoCodes';
import type { PromoCode, PromoCodeStatus } from '@/types/promoCode.types';
import { getPromoCodeStatus } from '@/types/promoCode.types';
import CreatePromoCodeModal from './CreatePromoCodeModal';
import PromoCodeDetailModal from './PromoCodeDetailModal';
import { ConfirmModal } from '@/components/ui/Modal';

const WHATSAPP_NUMBER = '447934514016';

type FilterType = 'all' | 'active' | 'inactive' | 'exhausted' | 'expired';

const filterOptions = [
  { value: 'all', label: 'Todos' },
  { value: 'active', label: 'Ativos' },
  { value: 'inactive', label: 'Inativos' },
  { value: 'exhausted', label: 'Esgotados' },
  { value: 'expired', label: 'Expirados' },
];

function getStatusConfig(status: PromoCodeStatus) {
  switch (status) {
    case 'active':
      return {
        label: 'Ativo',
        color: 'text-green-600 dark:text-green-400',
        bg: 'bg-green-100 dark:bg-green-900/30',
        icon: CheckCircle,
        dot: 'bg-green-500',
      };
    case 'inactive':
      return {
        label: 'Inativo',
        color: 'text-gray-600 dark:text-gray-400',
        bg: 'bg-gray-100 dark:bg-gray-800',
        icon: XCircle,
        dot: 'bg-gray-400',
      };
    case 'exhausted':
      return {
        label: 'Esgotado',
        color: 'text-yellow-600 dark:text-yellow-400',
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
        icon: AlertTriangle,
        dot: 'bg-yellow-500',
      };
    case 'expired':
      return {
        label: 'Expirado',
        color: 'text-red-600 dark:text-red-400',
        bg: 'bg-red-100 dark:bg-red-900/30',
        icon: Clock,
        dot: 'bg-red-500',
      };
  }
}

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function PromoCodeCard({
  code,
  onCopyLink,
  onViewDetails,
  onDeactivate,
}: {
  code: PromoCode;
  onCopyLink: (code: string) => void;
  onViewDetails: (id: string) => void;
  onDeactivate: (code: PromoCode) => void;
}) {
  const status = getPromoCodeStatus(code);
  const statusConfig = getStatusConfig(status);

  const durationText = code.durationMonths === 0
    ? 'Vitalicio'
    : `${code.durationMonths}m`;

  const usageText = code.maxUses
    ? `${code.usedCount}/${code.maxUses}`
    : `${code.usedCount}/ilim.`;

  return (
    <Card className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white font-mono">
              {code.code}
            </h3>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
              {statusConfig.label}
            </span>
          </div>
          {code.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {code.description}
            </p>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
        <span className="flex items-center gap-1.5">
          <Users className="w-4 h-4" />
          {usageText} usos
        </span>
        {code.expiresAt && (
          <span className="flex items-center gap-1.5">
            <Calendar className="w-4 h-4" />
            {status === 'expired' ? 'Expirou' : 'Expira'}: {formatDate(code.expiresAt)}
          </span>
        )}
        <span className="flex items-center gap-1.5">
          <Star className="w-4 h-4" />
          {code.plan.charAt(0).toUpperCase() + code.plan.slice(1)} {durationText}
        </span>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onCopyLink(code.code)}
          leftIcon={<Copy className="w-4 h-4" />}
        >
          Copiar Link
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewDetails(code.id)}
          leftIcon={<Eye className="w-4 h-4" />}
        >
          Ver Detalhes
        </Button>
        {code.isActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDeactivate(code)}
            leftIcon={<Trash2 className="w-4 h-4" />}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            Desativar
          </Button>
        )}
      </div>
    </Card>
  );
}

export default function PromoCodesListPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCodeId, setSelectedCodeId] = useState<string | null>(null);
  const [codeToDeactivate, setCodeToDeactivate] = useState<PromoCode | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const { data: codes, isLoading, isError } = usePromoCodes();
  const deactivateMutation = useDeactivatePromoCode();

  // Filter and search codes
  const filteredCodes = useMemo(() => {
    if (!codes) return [];

    return codes.filter((code) => {
      // Search filter
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !search ||
        code.code.toLowerCase().includes(searchLower) ||
        code.description?.toLowerCase().includes(searchLower);

      // Status filter
      const status = getPromoCodeStatus(code);
      const matchesFilter = filter === 'all' || status === filter;

      return matchesSearch && matchesFilter;
    });
  }, [codes, search, filter]);

  const handleCopyLink = async (code: string) => {
    const link = `https://wa.me/${WHATSAPP_NUMBER}?text=${code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleDeactivate = () => {
    if (!codeToDeactivate) return;

    deactivateMutation.mutate(codeToDeactivate.id, {
      onSuccess: () => {
        setCodeToDeactivate(null);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 dark:text-red-400">
          Erro ao carregar codigos promocionais
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Codigos Promocionais
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Gerencie codigos VIP e promocoes
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Novo Codigo
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Buscar codigo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterType)}
            options={filterOptions}
          />
        </div>
      </div>

      {/* Toast for copy */}
      {copiedCode && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-fade-in">
          Link copiado!
        </div>
      )}

      {/* Codes List */}
      {filteredCodes.length === 0 ? (
        <Card className="p-12 text-center">
          <Tag className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {search || filter !== 'all'
              ? 'Nenhum codigo encontrado'
              : 'Nenhum codigo criado'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {search || filter !== 'all'
              ? 'Tente ajustar os filtros'
              : 'Crie seu primeiro codigo promocional'}
          </p>
          {!search && filter === 'all' && (
            <Button onClick={() => setShowCreateModal(true)}>
              Criar Codigo
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredCodes.map((code) => (
            <PromoCodeCard
              key={code.id}
              code={code}
              onCopyLink={handleCopyLink}
              onViewDetails={setSelectedCodeId}
              onDeactivate={setCodeToDeactivate}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      <CreatePromoCodeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Detail Modal */}
      <PromoCodeDetailModal
        codeId={selectedCodeId}
        onClose={() => setSelectedCodeId(null)}
        onDeactivate={(code) => {
          setSelectedCodeId(null);
          setCodeToDeactivate(code);
        }}
      />

      {/* Deactivate Confirmation */}
      <ConfirmModal
        isOpen={!!codeToDeactivate}
        onClose={() => setCodeToDeactivate(null)}
        onConfirm={handleDeactivate}
        title="Desativar codigo"
        message={`Desativar codigo ${codeToDeactivate?.code}? Usuarios que ja ativaram nao serao afetados.`}
        confirmText="Desativar"
        cancelText="Cancelar"
        variant="danger"
        isLoading={deactivateMutation.isPending}
      />
    </div>
  );
}
